//@ts-check
import {emptyProductDetailsResponse} from '@/features/ProductReview/types'
import {getProductOverview} from '@/services/ProductsService/ProductOverview/productOverview'
import {updateProduct} from '@/services/ProductsService/product'
import {useAppDispatch} from '@/store'
import {setToastNotifications} from '@/store/slices/commonSlice'
import {ROUTES} from '@/utils/constants/routesConstants'
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react'
import {useNavigate, useParams} from 'react-router-dom'
import {PRODUCT_REVIEW_TAB_INDEX} from '../productReviewConstants'
import {getCurrentDateInTimezoneToUtc, isIsoDateInPast} from '@/helpers/dateUtils'

const EMPTY_PRODUCT_DATA = emptyProductDetailsResponse.data ?? {}

const cloneProductData = productData => {
  if (!productData) {
    return {}
  }

  if (typeof structuredClone === 'function') {
    return structuredClone(productData)
  }

  return JSON.parse(JSON.stringify(productData))
}

/**
 * @typedef {Object} ProductReviewContextValue
 * @property {import('@/features/ProductReview/types').ProductDetails} productData - Editable product details payload.
 * @property {boolean} isEditMode - Whether Product Review tabs are rendering editable controls.
 * @property {(isEditMode: boolean) => void} setIsEditMode - Toggles Product Review edit mode.
 * @property {(source: string, key: string, value: unknown, languageCode?: string) => void} updateEditableField - Updates one editable field in product data.
 * @property {(source: string, value: unknown) => void} setEditableField - Replaces an entire top-level field in product data (for example, an array field like `media`).
 * @property {(handler: ((productData: unknown) => Promise<unknown>) | null) => void} registerMediaSaveHandler - Registers a handler that runs (and may transform product data) right before saving, used to defer media uploads until the product is actually saved.
 * @property {() => void} resetEditableData - Resets editable data back to latest fetched payload.
 * @property {() => Promise<void>} saveEditableData - Saves editable data to backend and refreshes product query.
 * @property {boolean} isLoading - Whether the product details query is currently loading.
 * @property {boolean} isSaving - Whether a save request is in progress.
 * @property {unknown} saveError - Latest save error from update mutation.
 * @property {number} selectedTabIndex - Selected Product Review tab index.
 * @property {(selectedTabIndex: number) => void} setSelectedTabIndex - Updates selected Product Review tab index.
 */

/**
 * Product Review context.
 * @type {import('react').Context<ProductReviewContextValue | undefined>}
 */
export const ProductReviewContext = createContext(/** @type {ProductReviewContextValue | undefined} */ (undefined))

/**
 * Provides Product Review data to descendant components.
 *
 * Uses the route param `id` and queries product details:
 * - queryKey: ['PRODUCT', id]
 * - queryFn: getProductOverview(String(id))
 * - enabled: !!id
 *
 * @param {{children: import('react').ReactNode}} props - Provider props.
 * @returns {JSX.Element} Product Review context provider.
 */
export const ProductReviewProvider = ({children}) => {
  const {id} = useParams()
  const queryClient = useQueryClient()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const {data: queryData, isLoading} = useQuery({
    queryKey: ['PRODUCT', id],
    queryFn: () => getProductOverview(`${id}`),
    enabled: !!id,
  })

  const [editableProductData, setEditableProductData] = useState(EMPTY_PRODUCT_DATA)
  const [sourceProductData, setSourceProductData] = useState(EMPTY_PRODUCT_DATA)
  const [isEditMode, setIsEditMode] = useState(false)
  const [selectedTabIndex, setSelectedTabIndex] = useState(/** @type {number} */ (PRODUCT_REVIEW_TAB_INDEX.BASE_DATA))

  const updateProductMutation = useMutation({
    mutationFn: updateProduct,
    onSuccess: async (_response, variables) => {
      const cloned = cloneProductData(variables)
      setSourceProductData(cloned)
      setEditableProductData(cloneProductData(cloned))

      await queryClient.invalidateQueries({queryKey: ['PRODUCT', id]})
      dispatch(
        setToastNotifications({
          show: true,
          message: 'Product Successfully Updated',
          type: 'success',
        })
      )
      navigate(ROUTES.PRODUCTS_LIST)
    },
    onError: error => {
      const errorData = /** @type {{data?: {message?: string}}} */ (error)

      console.log(error)
      dispatch(
        setToastNotifications({
          show: true,
          message: `Error saving product: ${errorData?.data?.message || 'Unknown error'}`,
          type: 'error',
        })
      )
    },
  })
  const {mutateAsync: mutateUpdateProduct, isPending: isSaving, error: saveError} = updateProductMutation

  useEffect(() => {
    const latestProductData = queryData?.data ?? EMPTY_PRODUCT_DATA
    const cloned = cloneProductData(latestProductData)

    // Check if skuEndDate has passed and set isNewProduct to false
    if (cloned?.baseData?.skuEndDate && isIsoDateInPast(cloned.baseData.skuEndDate)) {
      cloned.baseData = {
        ...cloned.baseData,
        isNewProduct: false,
      }
    }

    setSourceProductData(cloned)
    setEditableProductData(cloneProductData(cloned))
    setIsEditMode(false)
    setSelectedTabIndex(PRODUCT_REVIEW_TAB_INDEX.BASE_DATA)
  }, [queryData])

  const updateEditableField = useCallback((source, key, value, languageCode) => {
    setEditableProductData(prev => {
      const next = cloneProductData(prev)
      const sourceData = next?.[source] ?? {}

      if (languageCode) {
        const localizedMap = sourceData?.[key] && typeof sourceData[key] === 'object' ? sourceData[key] : {}

        next[source] = {
          ...sourceData,
          [key]: {
            ...localizedMap,
            [languageCode]: value,
          },
        }

        return next
      }

      if (key === 'isNewProduct') {

        if (value === 'true' || value === true) {
          // set skuStartDate to current date and skuStartDate to current date + 90 days (CST time converted to UTC)
          next[source] = {
            ...sourceData,
            [key]: value,
            skuStartDate: getCurrentDateInTimezoneToUtc(),
            skuEndDate: getCurrentDateInTimezoneToUtc(90),
          }
          return next
        } 

        if (value === 'false' || value === false) {
          // set skuStartDate and skuEndDate to null
          next[source] = {
            ...sourceData,
            [key]: value,
            skuStartDate: null,
            skuEndDate: null,
          }
          return next
        }
        
        return next
      }

      next[source] = {
        ...sourceData,
        [key]: value,
      }

      // Check if skuEndDate has passed and isNewProduct should be false
      if (source === 'baseData' && next[source]?.skuEndDate && isIsoDateInPast(next[source].skuEndDate)) {
        next[source] = {
          ...next[source],
          isNewProduct: false,
        }
      }

      return next
    })
  }, [])

  const setEditableField = useCallback((source, value) => {
    setEditableProductData(prev => {
      const next = cloneProductData(prev)
      next[source] = value
      return next
    })
  }, [])

  const mediaSaveHandlerRef = useRef(/** @type {((productData: unknown) => Promise<unknown>) | null} */ (null))

  const registerMediaSaveHandler = useCallback(handler => {
    mediaSaveHandlerRef.current = handler
  }, [])

  const resetEditableData = useCallback(() => {
    setEditableProductData(cloneProductData(sourceProductData))
  }, [sourceProductData])

  const saveEditableData = useCallback(async () => {
    const dataToSave = mediaSaveHandlerRef.current
      ? await mediaSaveHandlerRef.current(editableProductData)
      : editableProductData

    await mutateUpdateProduct(dataToSave)
  }, [editableProductData, mutateUpdateProduct])

  const value = useMemo(
    () => ({
      productData: editableProductData ?? EMPTY_PRODUCT_DATA,
      isEditMode,
      setIsEditMode,
      updateEditableField,
      setEditableField,
      registerMediaSaveHandler,
      resetEditableData,
      saveEditableData,
      isLoading,
      isSaving,
      saveError,
      selectedTabIndex,
      setSelectedTabIndex,
    }),
    [
      editableProductData,
      isEditMode,
      isLoading,
      isSaving,
      registerMediaSaveHandler,
      resetEditableData,
      saveEditableData,
      saveError,
      selectedTabIndex,
      setEditableField,
      updateEditableField,
    ]
  )

  return <ProductReviewContext.Provider value={value}>{children}</ProductReviewContext.Provider>
}

/**
 * Product Review context consumer hook.
 * @returns {ProductReviewContextValue} Product review data and loading state.
 */
export const useProductReview = () => {
  const context = useContext(ProductReviewContext)

  if (!context) {
    throw new Error('useProductReview must be used within a ProductReviewProvider')
  }

  return context
}
